import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, FileText, Loader, Save } from 'lucide-react';
import { ghostBtn, HorizontalStepper, panelClass, primaryBtn } from '../governed-mcp-gateway/components/GatewayUi';
import { createEmptyForm, LLM_GATEWAY_PATHS, WIZARD_STEPS } from './llmGatewayConstants';
import { firstError, validateStep } from './llmGatewayForm';
import { logLlmGateway } from './llmGatewayLogger';
import { useLlmGatewayPage } from './LlmGatewayScreen';
import { gatewayToForm, useLlmGateway } from './llmGatewayStore';
import type { GatewayFormState } from './llmGatewayTypes';
import ApiTypeStep from './steps/ApiTypeStep';
import GovernanceStep from './steps/GovernanceStep';
import ReviewStep from './steps/ReviewStep';
import SelectLlmStep from './steps/SelectLlmStep';

export default function CreateGatewayPage() {
  const { isLight, onToast } = useLlmGatewayPage();
  const { gatewayId } = useParams<{ gatewayId: string }>();
  const navigate = useNavigate();
  const { getGateway, models, modelsError, modelsLoading, reloadModels, saveGateway } = useLlmGateway();
  const existing = gatewayId ? getGateway(gatewayId) : undefined;
  const isEdit = Boolean(gatewayId);

  const [form, setForm] = useState<GatewayFormState>(() => (existing ? gatewayToForm(existing) : createEmptyForm()));
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [missing, setMissing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!gatewayId) {
      logLlmGateway('app', 'create_wizard_open');
      return;
    }
    const found = getGateway(gatewayId);
    if (!found) {
      setMissing(true);
      logLlmGateway('error', 'edit_gateway_missing', { id: gatewayId });
      return;
    }
    setForm(gatewayToForm(found));
    logLlmGateway('app', 'edit_wizard_open', { id: gatewayId });
  }, [gatewayId, getGateway]);

  const goNext = () => {
    const current = validateStep(form, step, models);
    if (Object.keys(current).length) {
      setErrors(current);
      onToast('error', firstError(current) ?? 'Complete this step to continue.');
      return;
    }
    setErrors({});
    setStep((currentStep) => Math.min(currentStep + 1, WIZARD_STEPS.length - 1));
  };

  const persist = async (status: 'active' | 'draft' | 'disabled') => {
    const all = validateStep(form, 3, models);
    if (Object.keys(all).length) {
      setErrors(all);
      onToast('error', firstError(all) ?? 'Complete the gateway details before saving.');
      return;
    }
    setSaving(true);
    try {
      const saved = await saveGateway(form, { id: existing?.id, status });
      onToast('success', status === 'draft' ? 'Gateway saved as draft.' : isEdit ? 'Gateway updated.' : 'Gateway created.');
      navigate(status === 'draft' ? LLM_GATEWAY_PATHS.list : LLM_GATEWAY_PATHS.detail(saved.id));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not save this gateway.';
      logLlmGateway('error', 'gateway_save_failed', { message });
      onToast('error', message);
    } finally {
      setSaving(false);
    }
  };

  if (missing) {
    return (
      <div className={`${panelClass(isLight)} p-10 text-center`}>
        <p className={`text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>Gateway not found</p>
        <p className={`text-sm mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>This gateway may have been deleted.</p>
        <button type="button" className={`${primaryBtn()} mt-4`} onClick={() => navigate(LLM_GATEWAY_PATHS.list)}>
          Back to list
        </button>
      </div>
    );
  }

  const primaryLabel = isEdit && existing?.status !== 'draft' ? 'Save' : 'Create Gateway';

  return (
    <div className="space-y-5">
      <div className={`${panelClass(isLight)} px-5 py-4`}>
        <HorizontalStepper
          steps={WIZARD_STEPS.map((item) => ({ id: item.id, label: item.label }))}
          currentIndex={step}
          isLight={isLight}
          onSelect={(index) => {
            if (index <= step) setStep(index);
          }}
        />
      </div>

      <div className={`${panelClass(isLight)} p-5`}>
        {step === 0 && modelsLoading ? (
          <div className={`flex items-center justify-center py-16 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
            <Loader size={16} className="animate-spin mr-2" />
            Loading registered models…
          </div>
        ) : step === 0 && modelsError ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className={`text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>Could not load registered models</p>
            <p className={`text-sm mt-1 max-w-lg ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{modelsError}</p>
            <button type="button" className={`${primaryBtn()} mt-4`} onClick={() => void reloadModels()}>
              Retry
            </button>
          </div>
        ) : step === 0 ? (
          <SelectLlmStep form={form} errors={errors} isLight={isLight} models={models} onChange={(next) => { setForm(next); setErrors({}); }} />
        ) : null}
        {step === 1 ? <ApiTypeStep form={form} errors={errors} isLight={isLight} models={models} onChange={(next) => { setForm(next); setErrors({}); }} /> : null}
        {step === 2 ? <GovernanceStep form={form} errors={errors} isLight={isLight} onChange={(next) => { setForm(next); setErrors({}); }} /> : null}
        {step === 3 ? <ReviewStep form={form} isLight={isLight} models={models} /> : null}
        {step === 3 && Object.keys(errors).length ? (
          <p className="mt-4 text-xs text-red-500">{firstError(errors)}</p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          className={ghostBtn(isLight)}
          onClick={() => (step === 0 ? navigate(LLM_GATEWAY_PATHS.list) : setStep((current) => current - 1))}
        >
          <ChevronLeft size={14} />
          Back
        </button>
        <div className="flex flex-wrap items-center gap-2">
          {step === 3 ? (
            <>
              <button type="button" className={ghostBtn(isLight)} disabled={saving} onClick={() => void persist('draft')}>
                <FileText size={14} />
                Save as Draft
              </button>
              <button type="button" className={primaryBtn()} disabled={saving} onClick={() => void persist(existing?.status === 'disabled' ? 'disabled' : 'active')}>
                {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
                {primaryLabel}
              </button>
            </>
          ) : (
            <button
              type="button"
              className={primaryBtn()}
              disabled={step === 0 && (modelsLoading || Boolean(modelsError))}
              onClick={goNext}
            >
              Next
              <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
